import { TCloudVENDispatchType } from '../../pages/ven/components/CloudVenForm';
import { PRIMARY_COLOR } from '../../theme';
import { ICompany } from '../company/interface';
import { IFetchPageQuery } from '../IFetchQueryOptions';
import { ISite } from '../site/interface';

export const profileOptions = [
    { label: '2.0a', value: '2.0a' },
    { label: '2.0b', value: '2.0b' },
];

export interface IFetchVenQuery extends IFetchPageQuery {
    hideRelatedToClosedSites?: boolean;
    onlyAttachedToPrograms?: boolean;
}

export interface ITotalOverviewItem {
    online: number;
    offline: number;
    error: number;
    disabled: number;
}

export interface IOverviewItem {
    companyId: number;
    companyName: string;
    numbers: ITotalOverviewItem;
}

export interface IVenOverview {
    customerVen: {
        total: ITotalOverviewItem;
        byCompany: IOverviewItem[];
        byControlProvider: IOverviewItem[];
    };
    cloudVen?: {
        total: ITotalOverviewItem;
    };
}

export interface IVen {
    id: string;
    status: string;
    ven_username: string;
    ven_password: string;
    ven_id: string;
    ven_uri: string;
    ven_vtn_id: string;
    ven_market_context: string;
    support_price_and_clean_response_market_context: boolean;
    ven_group_id?: string;
    ven_party_id?: string;
    programs?: {
        program_id: number;
        program_utility: string;
        program_aggregator: string;
        program_market_context: string;
        ven_id: string;
        program_flag_requires_telemetry: number;
        program_date_created: Date;
        enrolled_sites_count: number;
        name: string;
    }[];

    sites?: ISite[];

    ven_cert: string;
    ven_key: string;
    ven_profile: string;
    ven_registration_id: string;
    ven_default_event_signal: number;
    ven_error: string;
    ven_detailed_logging: boolean;
    ven_enabled: boolean;
    ven_date_online: string;
    ven_date_modified: string;
    ven_date_created: string;
}

export interface ICloudVen extends Omit<IVen, 'ven_market_context' | 'ven_group_id' | 'ven_party_id'> {
    dispatch_type: TCloudVENDispatchType | null;
}

export interface ICreateCloudVen {
    dispatch_type: TCloudVENDispatchType;
    ven_username: string;
    ven_password: string;
    ven_id: string;
    ven_uri: string;
    ven_vtn_id: string;
    ven_cert: string;
    ven_key: string;
    ven_profile?: string;
    ven_registration_id?: string;
    ven_default_event_signal?: number;
    ven_detailed_logging?: boolean;
    ven_enabled?: boolean;

    site_ids?: number[];
    program_ids?: number[];
}

export interface IUpdateCloudVen {
    id: string;
    dispatch_type?: TCloudVENDispatchType;
    ven_id?: string;
    ven_uri?: string;
    ven_vtn_id?: string;
    ven_cert?: string;
    ven_key?: string;
    ven_profile?: string;
    ven_registration_id?: string;
    ven_default_event_signal?: number;
    ven_detailed_logging?: boolean;
    ven_enabled?: boolean;

    site_ids?: number[];
    program_ids?: number[];
}

export enum HardwareDeviceStatus {
    IDLE = 'idle',
    UPCOMING_EVENT = 'upcoming',
    PRE_EVENT = 'preevent',
    EVENT = 'event',
}

export enum HardwareServiceResult {
    OK = 'ok',
    SKIPPED = 'skipped',
    ERROR = 'error',
}

export interface IHardwareServiceState {
    result: HardwareServiceResult;
    timestamp: string;
    tunnel_started_at?: string;
    ping_ms?: number;
    gateway?: string;
    ip?: string;
    message?: string;
}

export interface IHardwareState {
    device_status: HardwareDeviceStatus;
    version: string;
    ngrok_version?: string;
    event_id?: string | null;
    event_start?: string | null;
    services?: Record<string, IHardwareServiceState>;
    published_at: string;
}

export interface ICustomerVen extends Omit<IVen, 'ven_cert' | 'ven_key' | 'ven_profile'> {
    company_id: number;
    company?: ICompany;
    site_id: number;
    control_provider_id?: number | null;
    control_provider?: ICompany | null;
    site?: ISite;
    mac_address?: string;
    hardware_state?: IHardwareState | null;
    ven_cert: null;
    ven_key: null;
    ven_profile: string | null;
}

export interface ICustomerVenControlProviders {
    company_id: number;
    company_name: string;
}

export enum VenStatus {
    ONLINE = 'online',
    OFFLINE = 'offline',
    DISABLED = 'disabled',
    ERROR = 'error',
}

export const VEN_STATUS_COLOR_MAP: { [key: string]: string } = {
    [VenStatus.DISABLED]: '#888787',
    [VenStatus.ERROR]: '#fac42a',
    [VenStatus.OFFLINE]: '#fa675c',
    [VenStatus.ONLINE]: PRIMARY_COLOR,
};

export interface ICloudVenCertificateDetails {
    fingerprints: {
        sha1: string;
        sha256: string;
    };
}
